import React from 'react';
import { StripeContext } from './StripeContext';
import { StripeElementProps } from './StripeElementProps';

const noop = () => {};

/**
 * Creates a Stripe Element component and listens to all their events
 * More info: https://stripe.com/payments/elements
 * Note: needs to render within a StripeContext otherwise a blank input is shown
 * @param type
 * @returns {*} a Stripe Element component of the given type
 */
export const createStripeElement = (type: stripe.elements.elementsType) => {
  const displayName = `${type}Element`;

  const StripeElement = ({
    id,
    className,
    options = {},
    onChange = noop,
    onBlur = noop,
    onFocus = noop,
    onEscape = noop,
    onReady = noop,
    onClick = noop,
    autoFocus,
    testID,
    ...restProps
  }: StripeElementProps) => {
    // requires the Stripe context
    // so  that we may use the stripe API to render the input
    const { elements } = React.useContext(StripeContext);
    const node = React.useRef(null);
    const elementRef = React.useRef(null);
    // Because stripeElement is outside react, we call mount() once and we call on('click'), etc
    // once. The function we attach is never updated, so it has to be a function that uses a ref
    // to get access to the actual handler for a given event that came from the most recent
    // rerender of the component. On render we update the callbacks in the ref'd object to store
    // the most recent handler, so we never call stale handlers.
    const cbPackRef = React.useRef({
      onChange,
      onBlur,
      onFocus,
      onEscape,
      onReady,
      onClick,
    });

    React.useEffect(() => {
      cbPackRef.current = {
        onChange,
        onBlur,
        onFocus,
        onEscape,
        onReady,
        onClick,
      };
    }, [onChange, onBlur, onFocus, onEscape, onReady, onClick]);

    const wrapCallback = (cbName: string) => (
      response: stripe.elements.ElementChangeResponse,
    ) => cbPackRef.current[cbName]({
      ...response,
      value: elementRef.current,
    });

    React.useEffect(
      () => () => {
        // destroy the element ref on unmount
        if (elementRef.current) {
          elementRef.current.destroy();
        }
      },
      [],
    );

    // have to use LayoutEffect because of the DOM
    React.useLayoutEffect(() => {
      if (elements && node.current && !elementRef.current) {
        const stripeElement = elements.create(type, options);
        elementRef.current = stripeElement;
        stripeElement.mount(node.current);
        // I'm keeping the click and the escape event here
        // because they were already in the code, however;
        // when I added the definitely typed library for stripe
        // these events started showing typings errors. I chose
        // to keep them because the previous author of this file
        // implemented them and I didn't want to break anything
        // @ts-ignore
        stripeElement.on('click', wrapCallback('onClick'));
        // @ts-ignore
        stripeElement.on('escape', wrapCallback('onEscape'));
        stripeElement.on('blur', wrapCallback('onBlur'));
        stripeElement.on('focus', wrapCallback('onFocus'));
        stripeElement.on('ready', wrapCallback('onReady'));
        stripeElement.on('change', wrapCallback('onChange'));

        stripeElement.focus();
      }
    }, [elements]);

    // update our element if the stripe context changes options
    React.useEffect(() => {
      // only update options if it's not empty
      if (Object.keys(options).length !== 0) {
        if (elementRef.current) {
          elementRef.current.update(options);
        }
      }
    }, [options]);

    return (
      <div
        data-private
        id={id}
        className={className}
        ref={node}
        data-testid={testID}
        {...restProps}
      />
    );
  };

  StripeElement.displayName = displayName;
  return StripeElement;
};
